import React, { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional page name for better error reporting */
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRetrying: boolean; // Added for branded transition
}

/**
 * Route-level error boundary that catches render errors in a single page
 * and shows a recoverable fallback instead of crashing the whole app.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isRetrying: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isRetrying: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[RouteErrorBoundary${this.props.pageName ? ` – ${this.props.pageName}` : ""}]`,
      error,
      info.componentStack,
    );
  }

  handleRetry = () => {
    // Show the branded spinner briefly to give a "premium" recovery feel
    this.setState({ isRetrying: true });
    
    setTimeout(() => {
      this.setState({ hasError: false, error: null, isRetrying: false });
    }, 800);
  };

  render() {
    // ── RETRYING STATE: Branded Spinner ──────────────────────────────────────
    if (this.state.isRetrying) {
      return (
        <div style={{
          minHeight: "60vh",
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fcf9f7',
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(30, 94, 91, 0.1)',
            borderTop: '4px solid #1e5e5b',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            marginBottom: '20px',
            boxSizing: 'border-box'
          }} />
          <div style={{ fontFamily: 'DM Sans, sans-serif', textAlign: 'center' }}>
            <p style={{
              color: '#0c2624',
              fontSize: '18px',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              margin: 0,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              BodyOwn
            </p>
          </div>
          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
          `}</style>
        </div>
      );
    }

    // ── ERROR STATE ──────────────────────────────────────────────────────────
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            fontFamily: "DM Sans, sans-serif",
            color: "#0C2624",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          {/* Static branded icon for consistency on the error screen */}
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #1e5e5b',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1rem',
            fontSize: '20px',
            opacity: 0.5
          }}>
            !
          </div>

          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700 }}>
            This page ran into a problem
          </h2>
          <p style={{ margin: 0, fontSize: "0.875rem", color: "#6b7280" }}>
            {this.props.pageName
              ? `Something went wrong on the ${this.props.pageName} page.`
              : "Something went wrong loading this page."}
          </p>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <button
              onClick={this.handleRetry}
              type="button"
              style={{
                padding: "0.5rem 1.5rem",
                borderRadius: "9999px",
                background: "#1E5E5B",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                fontWeight: 600
              }}
            >
              Retry
            </button>
            <button
              onClick={() => {
                window.location.href = "/dashboard";
              }}
              type="button"
              style={{
                padding: "0.5rem 1.5rem",
                borderRadius: "9999px",
                background: "#fff",
                color: "#1E5E5B",
                border: "1.5px solid #1E5E5B",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                fontWeight: 600
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;